import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Flame, Trophy, Target, Layers, ArrowRight, Sparkles, ListChecks, CalendarCheck, TrendingDown, GraduationCap, ExternalLink } from "lucide-react";
import EnquiryDialog from "@/components/EnquiryDialog";
import { Button } from "@/components/ui/button";
import { PageHeader, Loading, ErrorState, EmptyState } from "@/components/States";
import ProgressChart from "@/components/ProgressChart";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PlanUsage } from "@/components/PlanUsage";
import { UpgradeNote } from "@/components/UpgradeNote";
import { usePlan } from "@/lib/plan";

const Stat = ({ icon: Icon, label, value, testId, accent }) => (
  <div data-testid={testId} className="rounded-2xl border border-line bg-surface p-5">
    <Icon className={`h-5 w-5 ${accent || "text-aqua"}`} />
    <p className="mt-4 font-display text-3xl font-extrabold text-ink">{value}</p>
    <p className="text-xs text-ink-3">{label}</p>
  </div>
);

const Panel = ({ title, children, className = "" }) => (
  <div className={`rounded-2xl border border-line bg-surface p-6 ${className}`}>
    <h3 className="mb-4 font-bold text-ink">{title}</h3>{children}
  </div>
);

function recLink(r) {
  if (r.type === "explain") return `/learn?subject=${r.subject_id}&topic=${encodeURIComponent(r.topic)}`;
  if (r.type === "quiz") return `/quiz?subject=${r.subject_id}`;
  return "/flashcards";
}

function RecommendedCourses({ courses }) {
  const [enquire, setEnquire] = useState(null);
  if (!courses?.length) return null;
  return (
    <div data-testid="recommended-courses" className="mb-6 rounded-2xl bg-band p-6 text-white">
      <div className="mb-4 flex items-center gap-2"><GraduationCap className="h-5 w-5 text-[var(--brand-cyan)]" /><h3 className="font-bold">Recommended IDDCR programs</h3></div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {courses.map((c, i) => (
          <div key={c.id} data-testid={`recommended-course-${i}`} className="flex flex-col rounded-xl border border-white/10 bg-white/[0.05] p-4">
            <p className="text-xs text-[var(--brand-cyan)]">{c.reason}</p>
            <p className="mt-2 font-semibold">{c.title}</p>
            <p className="mt-1 text-xs text-ink-3">{[c.duration, c.mode].filter(Boolean).join(" · ")}</p>
            <div className="mt-auto flex flex-wrap items-center gap-3 pt-4">
              <Button size="sm" data-testid={`recommended-course-enquire-${i}`} onClick={() => setEnquire(c)} className="rounded-full bg-aqua-solid hover:bg-aqua-solid-hover">Enquire</Button>
              {c.enquiry_url && <a href={c.enquiry_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--brand-cyan)] hover:underline">Details <ExternalLink className="h-3 w-3" /></a>}
            </div>
          </div>
        ))}
      </div>
      <EnquiryDialog course={enquire} onClose={() => setEnquire(null)} />
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const { plan, reload: reloadPlan } = usePlan();
  const load = () => { setErr(""); api.get("/dashboard").then((r) => setD(r.data)).catch((e) => setErr(formatError(e))); };
  useEffect(load, []);
  if (err) return <ErrorState message={err} onRetry={load} />;
  if (!d) return <Loading label="Loading your dashboard..." />;

  return (
    <div data-testid="learner-dashboard">
      <PageHeader eyebrow="Learner dashboard" title={`Hello, ${user.name.split(" ")[0]}`} text="Here's how your clinical research learning is going.">
        <Button data-testid="dashboard-daily-btn" onClick={() => nav("/daily")} className="rounded-full">
          <CalendarCheck className="mr-2 h-4 w-4" />{d.daily_done ? "Daily quiz done" : "Take today's daily quiz"}
        </Button>
      </PageHeader>
      <PlanUsage plan={plan} onChange={reloadPlan} />
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat icon={Flame} label="Day streak" value={d.streak} testId="stat-streak" accent="text-orange-500" />
        <Stat icon={Trophy} label="Quizzes completed" value={d.quizzes_completed} testId="stat-quizzes" />
        <Stat icon={Target} label="Average score" value={`${d.average_score}%`} testId="stat-average" />
        <Stat icon={Layers} label="Flashcard decks" value={d.flashcard_decks} testId="stat-decks" />
      </div>
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Panel title="Subject-wise performance" className="lg:col-span-2">{d.locked?.includes("subject_performance") ? <UpgradeNote testId="locked-subject-performance" text="Subject-wise performance tracking is available on LearnAI Plus and Pro." /> : <ProgressChart data={d.subject_performance} />}</Panel>
        <Panel title="Quick actions">
          <div className="flex flex-col gap-2">
            {[["/learn", Sparkles, "Get an AI explanation", "quick-learn"], ["/quiz", ListChecks, "Generate a quiz", "quick-quiz"], ["/flashcards", Layers, "Open flashcards", "quick-flashcards"], ["/daily", CalendarCheck, "Daily quiz", "quick-daily"]].map(([to, Icon, l, t]) => (
              <Link key={to} to={to} data-testid={t} className="group flex items-center gap-3 rounded-lg border border-line px-4 py-3 text-sm font-medium text-ink-2 transition-colors hover:border-aqua hover:text-ink">
                <Icon className="h-4 w-4 text-aqua" />{l}<ArrowRight className="ml-auto h-4 w-4 opacity-0 transition-opacity group-hover:opacity-100" />
              </Link>
            ))}
          </div>
        </Panel>
      </div>
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Panel title="Weak topics">
          {d.locked?.includes("weak_topics") ? <UpgradeNote compact testId="locked-weak-topics" text="Weak-topic detection is included in LearnAI Plus and Pro." /> : d.weak_topics.length === 0 ? (
            <p data-testid="weak-topics-empty" className="text-sm text-ink-3">No weak topics yet. Topics with 3+ answers and under 60% accuracy will appear here.</p>
          ) : (
            <ul data-testid="weak-topics-list" className="flex flex-col gap-2">
              {d.weak_topics.map((w, i) => (
                <li key={i} className="flex items-center gap-3 rounded-lg bg-amber-50/70 px-4 py-3">
                  <TrendingDown className="h-4 w-4 text-amber-600" />
                  <div className="flex-1"><p className="text-sm font-semibold text-ink">{w.topic}</p><p className="text-xs text-ink-3">{w.subject_name} · {w.answered} answers</p></div>
                  <span className="font-mono text-sm font-semibold text-amber-700">{w.accuracy}%</span>
                </li>
              ))}
            </ul>
          )}
        </Panel>
        <Panel title="Recommended for you">
          {d.locked?.includes("recommendations") ? <UpgradeNote compact testId="locked-recommendations" text="Personalised revision recommendations are part of LearnAI Pro." /> : d.recommendations.length === 0 ? <p className="text-sm text-ink-3">You're on track. Try a harder difficulty level!</p> : (
            <ul data-testid="recommendations-list" className="flex flex-col gap-2">
              {d.recommendations.map((r, i) => (
                <li key={i}><Link data-testid={`recommendation-${i}`} to={recLink(r)} className="flex items-center gap-3 rounded-lg border border-line px-4 py-3 text-sm text-ink-2 hover:border-aqua">
                  <Sparkles className="h-4 w-4 text-aqua" />{r.text}<ArrowRight className="ml-auto h-4 w-4" /></Link></li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
      <div className="mt-6">
        <RecommendedCourses courses={d.recommended_courses} />
        <Panel title="Recent attempts">
          {d.recent_attempts.length === 0 ? (
            <EmptyState testId="recent-attempts-empty" title="No quizzes yet" text="Generate your first quiz to start tracking progress." action="Start a quiz" onAction={() => nav("/quiz")} />
          ) : (
            <div className="divide-y divide-line">
              {d.recent_attempts.map((a) => (
                <Link key={a.id} to={`/history/${a.id}`} data-testid={`recent-attempt-${a.id}`} className="flex items-center gap-4 py-3 text-sm hover:bg-canvas-2">
                  <div className="flex-1"><p className="font-semibold text-ink">{a.subject_name}</p><p className="text-xs text-ink-3">{a.topic} · <span className="capitalize">{a.difficulty}</span> · {new Date(a.created_at).toLocaleDateString()}</p></div>
                  <span className="font-mono font-semibold text-ink">{a.score}/{a.total}</span>
                  <span className={`w-14 text-right font-mono text-xs ${a.percentage >= 60 ? "text-emerald-600" : "text-amber-600"}`}>{a.percentage}%</span>
                </Link>
              ))}
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}
